import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const ROTATION_MS = 5000;

/**
 * Auto-rotating Featured Products spotlight for the homepage.
 * Same UX pattern as SponsorSpotlight: 5s rotation, manual dots, pause-on-hover.
 * Hides if there are no products yet.
 */
export default function FeaturedProducts() {
    const [products, setProducts] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        api.get("/products")
            .then((r) => setProducts(r.data || []))
            .catch(() => setProducts([]));
    }, []);

    const items = useMemo(
        () => (products || []).filter((p) => p.image_url && p.is_published),
        [products],
    );

    useEffect(() => {
        if (paused || items.length <= 1) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATION_MS);
        return () => clearInterval(t);
    }, [items.length, paused]);

    if (products === null || items.length === 0) return null;
    const cur = items[idx % items.length];

    return (
        <section
            className="relative border-y border-line overflow-hidden bg-bg-soft"
            data-testid="featured-products"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="absolute inset-0 opacity-40 bg-gradient-to-l from-bg via-purple-dark/40 to-bg" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
                <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center">
                    {/* LEFT: rotating product image */}
                    <div className="md:col-span-7">
                        <Link to={`/store/${cur.slug}`} className="block" data-testid="featured-image-link">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={cur.id}
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    className="aspect-[4/3] w-full bg-bg-soft border border-gold/30 overflow-hidden hover:border-gold transition-colors relative"
                                >
                                    <div
                                        className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                                        style={{ backgroundImage: `url('${cur.image_url}')` }}
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </Link>
                    </div>

                    {/* RIGHT: name, price, CTA, dots */}
                    <div className="md:col-span-5">
                        <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-3 flex items-center gap-2">
                            <ShoppingBag size={14} /> Featured · Royal Store
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={cur.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.45 }}
                            >
                                <div className="text-ink text-[10px] tracking-[0.3em] uppercase font-bold mb-2">{cur.category}</div>
                                <h2 className="h-display text-3xl md:text-4xl text-white leading-[0.95]" data-testid="featured-name">
                                    {cur.name}
                                </h2>
                                <div className="text-gold text-2xl font-bold mt-4" data-testid="featured-price">
                                    ${(cur.price_cents / 100).toFixed(2)}
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <div className="mt-7 flex flex-wrap items-center gap-6">
                            <Link
                                to={`/store/${cur.slug}`}
                                data-testid="featured-cta"
                                className="btn-gold px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2"
                            >
                                Shop Now <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/store"
                                className="text-white/70 text-xs tracking-[0.3em] uppercase font-bold hover:text-gold"
                            >
                                All Products →
                            </Link>
                        </div>

                        <div className="flex gap-2 mt-7" data-testid="featured-dots">
                            {items.map((p, i) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    aria-label={`Show ${p.name}`}
                                    onClick={() => setIdx(i)}
                                    data-testid={`featured-dot-${i}`}
                                    className={`h-1.5 transition-all ${i === idx ? "w-8 bg-gold" : "w-3 bg-line hover:bg-white/40"}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
